import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { PROCESS_STEPS } from '../../mocks/sharedData';

interface Step {
  number: string;
  title: string;
  description: string;
  image?: string;
}

interface ProcessTimelineProps {
  eyebrow?: string;
  headline?: string;
  steps?: Step[];
}

const STEP_IMAGES = [
  'https://public.readdy.ai/ai/img_res/0eab5025f3c3f1e77d567d44148bfe17.jpg',
  'https://readdy.ai/api/search-image?query=solar%20engineer%20designing%20solar%20panel%20layout%20on%20computer%20screen%20showing%203d%20roof%20model%20professional%20office%20modern%20workspace%20clean%20white%20desk&width=400&height=300&seq=proc02&orientation=landscape',
  'https://readdy.ai/api/search-image?query=official%20permit%20documents%20blueprints%20folder%20on%20desk%20professional%20hands%20signing%20paperwork%20clean%20modern%20office%20natural%20light%20warm%20tones&width=400&height=300&seq=proc03&orientation=landscape',
  'https://readdy.ai/api/search-image?query=solar%20installation%20crew%20installing%20solar%20panels%20on%20residential%20roof%20blue%20sky%20sunny%20day%20professional%20equipment%20safety%20gear%20high%20quality%20photography&width=400&height=300&seq=proc04&orientation=landscape',
];

const ProcessTimeline = ({
  eyebrow = 'How It Works',
  headline = 'Your Seamless Journey to Solar',
  steps = PROCESS_STEPS,
}: ProcessTimelineProps) => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="bg-zinc-950 py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-20 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-10 h-px bg-amber-500" />
            <span className="text-amber-400 text-xs font-semibold tracking-[0.25em] uppercase">{eyebrow}</span>
            <div className="w-10 h-px bg-amber-500" />
          </div>
          <h2 className="font-serif text-5xl md:text-6xl text-white leading-tight max-w-2xl mx-auto">{headline}</h2>
          <p className="text-zinc-400 text-lg mt-5 max-w-xl mx-auto">
            From your first call to the day your system powers on — we manage every detail.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <div
              key={step.number}
              className={`group transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              {/* Card */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden hover:border-amber-500/30 transition-colors duration-300">
                {/* Image */}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={STEP_IMAGES[i]}
                    alt={step.title}
                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent" />
                  {/* Step number overlay */}
                  <div className="absolute bottom-4 left-5 flex items-center gap-3">
                    <span className="font-serif text-5xl text-amber-400/30 font-bold leading-none">{step.number}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Amber number badge */}
                  <div className="inline-flex items-center justify-center w-8 h-8 bg-amber-500 rounded-full mb-4">
                    <span className="text-zinc-950 text-xs font-bold">{step.number}</span>
                  </div>
                  <h3 className="font-serif text-xl text-white font-semibold mb-3">{step.title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>

              {/* Connector arrow (not on last) */}
              {i < steps.length - 1 && (
                <div className="hidden lg:flex justify-end items-center pr-1 pt-2 opacity-30">
                  {/* done via CSS gap and border */}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          className={`text-center mt-16 transition-all duration-700 delay-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <a
            href="https://meetings.hubspot.com/starsolarspecialists/solarpowerconsultation"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-sm px-10 py-5 rounded-full whitespace-nowrap transition-all duration-300 cursor-pointer"
          >
            Start Step 1 — Free Consultation
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProcessTimeline;
